/**
 * The API keys of Saidiseire's slow jobs, as CRM Settings > Integrations reads
 * and writes them (`crm-monitoring/keys.routes.ts`,
 * docs/saidiseire-kiirus-turve-raport.md): the PageSpeed key behind the weekly
 * speed test and the Wordfence key behind the vulnerability feed, and the
 * PageSpeed call budget (the limits a manager sets, and what was used).
 *
 * A HAND-WRITTEN MIRROR of `MonitorKeysView`, `PagespeedBudgetView` and
 * `VulnFeedStateView` in `apps/api/src/modules/crm-monitoring/types.ts`;
 * `monitoringKeys.test.ts` reads that file and compares the field names, and
 * holds `PAGESPEED_LIMIT_BOUNDS` and the per-minute numbers to
 * `speed/budget.ts` and `speed/job.ts`.
 *
 * A key never travels back to the browser: the server says whether one is
 * stored, never what it is.
 *
 * React-free.
 */
import { api } from "./api";
import type { SpeedLimit } from "./monitoringSpeed";

// ── Contract (mirrors crm-monitoring/types.ts) ─────────────────────────────────

/** Where the vulnerability feed stands. */
export interface VulnFeedStateView {
  /** The last download stored completely, or null. */
  fetchedAt: string | null;
  /** The last download started, or null. */
  attemptAt: string | null;
  /** Records stored after the last complete download. */
  entries: number;
  /** Why the last attempt failed (bad_key, rate_limited, fetch_failed, parse_failed, store_failed, timeout), or null. */
  error: string | null;
}

/** The PageSpeed call budget: the limits, what was used against them, and whether measuring has stopped. */
export interface PagespeedBudgetView {
  /** Calls allowed per day; the day is Pacific time, when Google resets the daily quota. */
  perDay: number;
  /** Calls allowed per Tallinn calendar month. */
  perMonth: number;
  /** Calls counted today (Pacific day). */
  dayCalls: number;
  /** Calls counted this month (Tallinn month). */
  monthCalls: number;
  /** The part used up by the engine's own rule (fewer than one site's two calls left), or null. Decided by the API. */
  spent: SpeedLimit | null;
}

/** The slow jobs' API keys: only whether each is stored. */
export interface MonitorKeysView {
  pagespeed: { keySet: boolean; budget: PagespeedBudgetView };
  wordfence: { keySet: boolean; feed: VulnFeedStateView };
}

export interface PagespeedLimits {
  perDay: number;
  perMonth: number;
}

/** Empty or null clears a key; a field left out keeps it. The limits go together or not at all. */
export interface MonitorKeysPatch {
  pagespeedApiKey?: string | null;
  wordfenceApiKey?: string | null;
  pagespeedLimits?: PagespeedLimits;
}

/** `${MONITORING_PATH}/keys` (a test holds the two equal; the literal keeps Settings off the monitoring module). */
export const MONITOR_KEYS_PATH = "/admin/crm/monitoring/keys";

/** The route's own limit on a key's length. */
export const MONITOR_KEY_MAX = 400;

export const getMonitorKeys = (): Promise<MonitorKeysView> => api.get<MonitorKeysView>(MONITOR_KEYS_PATH);

export const saveMonitorKeys = (patch: MonitorKeysPatch): Promise<MonitorKeysView> =>
  api.put<MonitorKeysView>(MONITOR_KEYS_PATH, patch);

// ── PageSpeed call budget ────────────────────────────────────────────────────

/** What the API accepts (`SPEED_LIMIT_BOUNDS` in speed/budget.ts): at least one whole site, two calls. */
export const PAGESPEED_LIMIT_BOUNDS = {
  perDay: { min: 2, max: 100_000 },
  perMonth: { min: 2, max: 10_000_000 },
} as const;

/**
 * A limit as typed: a whole number inside its bounds, or null. Spaces are
 * allowed, the no-break ones included ("25 000" is how the card prints it);
 * a decimal point or comma is not.
 */
export function parseLimitInput(field: keyof PagespeedLimits, text: string): number | null {
  const digits = text.replace(/\s/g, "");
  if (!/^\d+$/.test(digits)) return null;
  const value = Number(digits);
  const { min, max } = PAGESPEED_LIMIT_BOUNDS[field];
  return Number.isSafeInteger(value) && value >= min && value <= max ? value : null;
}

/** PageSpeed calls one site's run makes, mobile and desktop (`SPEED_CALLS_PER_SITE` in speed/budget.ts). */
export const PAGESPEED_CALLS_PER_SITE = 2;

/** Google's own per-minute limit on the key (`GOOGLE_CALLS_PER_MINUTE` in speed/budget.ts); nothing counts it. */
export const PAGESPEED_CALLS_PER_MINUTE = 30;

/**
 * The most calls the weekly job makes in one five-minute wake
 * (`SPEED_SITES_PER_WAKE` sites in speed/job.ts, two calls each), so the
 * per-minute limit is never near.
 */
export const PAGESPEED_CALLS_PER_WAKE = 4;

/** A limit no number of whole sites uses up: an odd one leaves one call unused. */
export function limitLeavesUnused(limit: number | null): boolean {
  return limit !== null && limit % PAGESPEED_CALLS_PER_SITE !== 0;
}

/**
 * The note under the usage line: which part of the budget is spent, as the
 * API decided it (`spent`), so "29 / 30" reads as spent here just as it does
 * to the job and the site card. Null: a whole site still fits.
 */
export function spentNoteKey(spent: SpeedLimit | null): "settings.pagespeed.daySpent" | "settings.pagespeed.monthSpent" | null {
  if (spent === "month") return "settings.pagespeed.monthSpent";
  if (spent === "day") return "settings.pagespeed.daySpent";
  return null;
}

// ── Rules ────────────────────────────────────────────────────────────────────

export type FeedErrorKey = "badKey" | "rateLimited" | "fetchFailed" | "parseFailed" | "storeFailed" | "timeout";

const FEED_ERRORS: Record<string, FeedErrorKey> = {
  bad_key: "badKey",
  rate_limited: "rateLimited",
  fetch_failed: "fetchFailed",
  parse_failed: "parseFailed",
  store_failed: "storeFailed",
  timeout: "timeout",
};

/** The feed's last error as a key under `settings.wordfence.feedErrors`, or null for none or an unknown code (shown as it came). */
export function feedErrorKey(code: string | null): FeedErrorKey | null {
  return code ? (FEED_ERRORS[code] ?? null) : null;
}

/** Every feed error code the API may store, for the catalogue test. */
export const FEED_ERROR_CODES = Object.keys(FEED_ERRORS);

/**
 * The Wordfence card's status pill. "Ready" only once a key is stored AND the
 * feed was downloaded AND the last attempt did not fail: a key alone matches
 * nothing yet. A key with no download is waiting (neutral); a failed last
 * attempt is a warning, whether or not an older download is still there.
 */
export type WordfenceStatus = "off" | "waiting" | "failing" | "ready";

export function wordfenceStatus(wf: MonitorKeysView["wordfence"]): { status: WordfenceStatus; tone: "ok" | "wt" | "mut" } {
  if (!wf.keySet) return { status: "off", tone: "mut" };
  if (wf.feed.error) return { status: "failing", tone: "wt" };
  if (!wf.feed.fetchedAt) return { status: "waiting", tone: "mut" };
  return { status: "ready", tone: "ok" };
}
